import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Bookmark, PackageOpen, Home } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { StatusBadge, fmtMoney, fmtDateDow } from "@/components/status";

const PREVIOUS = ["delivered", "cancelled"];

export default function OrderHistory() {
  const navigate = useNavigate();
  const { replace } = useCart();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("current");
  const [search, setSearch] = useState("");

  const load = () => api.orders("all").then(setOrders).catch(() => {});
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => orders.filter((o) => {
    const isPrev = PREVIOUS.includes(o.status);
    if (tab === "current" && isPrev) return false;
    if (tab === "previous" && !isPrev) return false;
    if (tab === "standing" && !o.is_standing) return false;
    const q = search.trim().toLowerCase();
    if (q && !(`${o.id} ${(o.items || []).map((i) => i.name).join(" ")}`.toLowerCase().includes(q))) return false;
    return true;
  }), [orders, tab, search]);

  const currentCount = orders.filter((o) => !PREVIOUS.includes(o.status)).length;
  const prevCount = orders.filter((o) => PREVIOUS.includes(o.status)).length;
  const standingCount = orders.filter((o) => o.is_standing).length;

  const reorder = async (o) => {
    try {
      const fresh = await api.catalogue();
      const byId = Object.fromEntries(fresh.map((f) => [f.id, f]));
      const list = (o.items || []).map((i) => ({ flower: byId[i.flower_id] || { id: i.flower_id, name: i.name, price: i.price, unit: i.unit, pack_size: i.pack_size, pack_unit: i.pack_unit, image_url: i.image_url }, qty: i.quantity }));
      replace(list);
      toast.success("Added to cart — review & confirm");
      navigate("/store/cart");
    } catch (err) { toast.error("Could not reorder"); }
  };
  const toggleStanding = async (o) => {
    try { const d = await api.toggleStanding(o.id); setOrders((l) => l.map((x) => (x.id === o.id ? d : x))); toast.success(d.is_standing ? "Saved as standing order" : "Removed standing order"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="animate-fade-up">
      <h1 className="mb-3 font-serif text-2xl font-extrabold">My Orders</h1>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button data-testid="orders-tab-current" onClick={() => setTab("current")} className={`rounded-full px-4 py-2 text-sm font-bold ${tab === "current" ? "bg-[#EBBAD1] text-[#28282E]" : "bg-white/10 text-white/90"}`}>Current · {currentCount}</button>
        <button data-testid="orders-tab-previous" onClick={() => setTab("previous")} className={`rounded-full px-4 py-2 text-sm font-bold ${tab === "previous" ? "bg-[#EBBAD1] text-[#28282E]" : "bg-white/10 text-white/90"}`}>Previous · {prevCount}</button>
        <button data-testid="orders-tab-standing" onClick={() => setTab("standing")} className={`rounded-full px-4 py-2 text-sm font-bold ${tab === "standing" ? "bg-[#EBBAD1] text-[#28282E]" : "bg-white/10 text-white/90"}`}>Standing · {standingCount}</button>
        <button data-testid="orders-home-button" onClick={() => navigate("/store")} className="ml-auto flex items-center gap-1.5 rounded-full bg-[#28282E] px-4 py-2 text-sm font-bold text-white hover:bg-[#1F1F24]"><Home className="h-4 w-4" /> Home</button>
      </div>
      <input data-testid="orders-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by flower name or order #"
        className="mb-4 w-full rounded-full border border-white/20 bg-black/15 px-4 py-2.5 text-sm text-white placeholder:text-white/78 outline-none focus:border-[#EBBAD1]" />

      {shown.length === 0 ? (
        <div className="rounded-2xl bg-white/10 py-16 text-center"><PackageOpen className="mx-auto h-10 w-10 text-white/65" /><p className="mt-2 text-sm text-white/85">No {tab} orders.</p></div>
      ) : (
        <div className="space-y-3">
          {shown.map((o) => (
            <div key={o.id} data-testid={`order-card-${o.id}`} className="rounded-2xl bg-white/10 p-4">
              <div className="flex items-center gap-2">
                <StatusBadge status={o.status} />
                {o.is_standing ? <span className="inline-flex items-center gap-1 rounded-full bg-[#EBBAD1] px-2 py-0.5 text-[10px] font-bold uppercase text-[#28282E]"><Bookmark className="h-3 w-3" /> Standing</span> : null}
                <span className="ml-auto font-mono text-xs text-white/82">#{String(o.id).slice(0, 8).toUpperCase()}</span>
              </div>
              <div className="mt-2 text-sm text-white/90">
                {o.delivery_estimate ? `Delivery ${fmtDateDow(o.delivery_estimate)}` : "Delivery TBC"}
                {o.placed_by_name ? ` · Placed by ${o.placed_by_name}` : ""}
              </div>
              <div className="mt-2 space-y-1 border-t border-white/10 pt-2">
                {(o.items || []).slice(0, 6).map((i, idx) => (
                  <div key={idx} className="flex justify-between text-sm"><span className="text-white/90">{i.quantity}× {i.name}</span><span className="text-white/85">{fmtMoney(i.price * i.quantity)}</span></div>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between border-t border-white/10 pt-2">
                <span className="text-sm font-bold">{fmtMoney(o.total)}</span>
                <div className="flex gap-2">
                  <button data-testid={`order-reorder-${o.id}`} onClick={() => reorder(o)} className="flex items-center gap-1 rounded-full bg-[#28282E] px-3.5 py-1.5 text-xs font-bold text-white hover:bg-[#1F1F24]"><RefreshCw className="h-3.5 w-3.5" /> {tab === "standing" ? "Edit" : "Reorder"}</button>
                  <button data-testid={`order-standing-${o.id}`} onClick={() => toggleStanding(o)} className="flex items-center gap-1 rounded-full bg-white/15 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-white/25"><Bookmark className="h-3.5 w-3.5" /> {o.is_standing ? "Pause" : "Standing"}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
